// ==========================================================================
// screens/Kitchen.tsx — shopping list and recipe book (FF-051, 10 Oct 2026)
//   James: "lets go with Shopping list and keen to see what you can do with
//   it, have a option to have saved receipes like a receipe book either from a
//   website, screeen shot on a phone or a custome text one we create with chat
//   into a MD file."
//   List     quick add ("500 g mince, milk, 2 loaves bread"), sorted into the
//            sections of the shop in walking order, tick into the trolley,
//            clear when home, one-tap re-add of things you buy often.
//   Recipes  the book: add from a website link (the page's own recipe data,
//            no AI), a photo or screenshot ("Read it" on request only), or
//            typed / pasted Markdown (e.g. written with Claude). Open one to
//            scale it, send its ingredients to the list, or copy it out as .md.
// ==========================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen, Camera, Check, ChefHat, ClipboardCopy, Clock, Download, ExternalLink, FileText, Globe, Heart, Image as ImageIcon,
  ChevronRight, LayoutGrid, List, Loader2, Minus, ShoppingCart, Pencil, Plus, ScanText, Search, ShoppingBasket, Sparkles, Trash2, Users, X,
} from "lucide-react";
import ChapterHeader from "@/components/ChapterHeader";
import MealPlan from "@/screens/MealPlan";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { ErrorText } from "@/components/ui";
import { useWorkerFile } from "@/lib/receipts";
import { tokenColour, ymd } from "@/lib/calendar";
import { usePeople } from "@/lib/household";
import { useShopTrip, useToggleTask, type Task } from "@/lib/tasks";
import TaskSheet from "@/components/TaskSheet";
import { format, parseISO } from "date-fns";
import {
  MD_TEMPLATE, SECTIONS, scaleQty, guessSection, importRecipe, parseMarkdown, readRecipePhoto, sectionLabel, splitIngredient, toMarkdown,
  TAB_COLOURS, hasTab, useArchiveTab, useRecipeTabs, useSaveTab, type RecipeTab, type TabColour,
  useAddRecipePhoto, useAddShopping, useArchiveRecipe, useClearTicked, useRecentItems, useRecipes, useSaveRecipe, useShopping, useUpdateShopping,
  type Ingredient, type Recipe, type RecipeDraft, type Section, type ShopItem,
} from "@/lib/kitchen";

const pill = ({ isActive }: { isActive: boolean }) => `rounded-full px-4 py-2 text-sm transition ${isActive ? "bg-ink text-bg font-semibold" : "text-ink-muted hover:text-ink"}`;
const btn = "inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition disabled:opacity-50";
const primaryBtn = `${btn} bg-primary text-primary-ink hover:opacity-90`;
const quietBtn = `${btn} bg-surface-raised text-ink hover:bg-line/60`;

export default function Kitchen() {
  return (
    <>
      <ChapterHeader chapter="kitchen" title="Kitchen" subtitle="The shopping list we both see, the family recipe book, and what's for dinner this week." />
      <nav className="mb-7 flex flex-wrap gap-2">
        <NavLink to="/kitchen/list" className={pill}>Shopping list</NavLink>
        <NavLink to="/kitchen/recipes" className={pill}>Recipes</NavLink>
        <NavLink to="/kitchen/plan" className={pill}>Meal plan</NavLink>
      </nav>
      <Routes>
        <Route path="list" element={<ShoppingList />} />
        <Route path="recipes" element={<RecipeBook />} />
        <Route path="plan" element={<MealPlan />} />
        <Route path="*" element={<Navigate to="list" replace />} />
      </Routes>
    </>
  );
}

// ==========================================================================
// Shopping list
// ==========================================================================
/** "500 g mince, milk, 2 loaves bread" → three items, each with its own qty and section. */
function parseQuick(text: string) {
  return text.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean).map((s) => {
    const { qty, item } = splitIngredient(s);
    return { name: item, qty, section: guessSection(item) };
  });
}

function ShoppingList() {
  const list = useShopping(); const recent = useRecentItems();
  const add = useAddShopping(); const upd = useUpdateShopping(); const clear = useClearTicked();
  const [text, setText] = useState(""); const [section, setSection] = useState<Section | "">("");
  const [editing, setEditing] = useState<ShopItem | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const { trip } = useShopTrip(); const toggleTask = useToggleTask(); const people = usePeople();
  const [tripSheet, setTripSheet] = useState(false);

  const items = list.data ?? [];
  const open = items.filter((i) => !i.checked_at);
  const ticked = items.filter((i) => i.checked_at).sort((a, b) => (b.checked_at ?? "").localeCompare(a.checked_at ?? ""));
  const preview = parseQuick(text);
  const onList = new Set(open.map((i) => i.name.trim().toLowerCase()));
  const chips = (recent.data ?? []).filter((r) => !onList.has(r.name.toLowerCase())).slice(0, 14);

  const submit = () => {
    if (!preview.length) return;
    add.mutate(preview.map((p) => ({ ...p, section: preview.length === 1 && section ? section : p.section })));
    setText(""); setSection(""); input.current?.focus();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 space-y-5">
        <TripBanner trip={trip} people={people.data ?? []} toGet={open.length} inTrolley={ticked.length}
          onPlan={() => setTripSheet(true)}
          onDone={() => { if (trip) toggleTask.mutate(trip, { onSuccess: () => { if (ticked.length) clear.mutate(); } }); }} busy={toggleTask.isPending || clear.isPending} />
        {/* Quick add */}
        <div className="rounded-lg bg-surface p-4 shadow-soft ring-1 ring-line/70">
          <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex gap-2">
            <input ref={input} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add… e.g. 500 g mince, milk, nappies"
              className={`${inputCls} min-w-0 flex-1 text-base`} enterKeyHint="done" autoComplete="off" />
            <button type="submit" disabled={!preview.length || add.isPending} className={`${primaryBtn} shrink-0 px-5`} aria-label="Add"><Plus className="h-5 w-5" /></button>
          </form>
          {preview.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-xs">
              {preview.length === 1 ? (
                <>
                  <span className="text-ink-muted">Goes in</span>
                  <select value={section || preview[0].section} onChange={(e) => setSection(e.target.value as Section)} className="rounded-full border border-line bg-bg px-2.5 py-1 text-xs">
                    {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                  {preview[0].qty && <span className="rounded-full bg-surface-raised px-2.5 py-1">{preview[0].qty}</span>}
                </>
              ) : (
                <>
                  <span className="text-ink-muted">{preview.length} items:</span>
                  {preview.map((p, i) => <span key={i} className="rounded-full bg-surface-raised px-2.5 py-1">{p.qty ? `${p.qty} ` : ""}{p.name} · <span className="text-ink-muted">{sectionLabel(p.section)}</span></span>)}
                </>
              )}
            </div>
          )}
          <ErrorText>{add.error?.message}</ErrorText>
        </div>

        {/* The list, in walking order */}
        {list.isLoading && <p className="text-sm text-ink-muted">Loading the list…</p>}
        {!list.isLoading && open.length === 0 && (
          <div className="rounded-lg bg-surface p-8 text-center shadow-soft ring-1 ring-line/70">
            <ShoppingBasket className="mx-auto h-10 w-10 text-ink-muted" />
            <p className="mt-3 font-semibold">{ticked.length ? "All in the trolley" : "Nothing on the list"}</p>
            <p className="text-sm text-ink-muted">{ticked.length ? "Clear the ticked ones when you're home." : "Add things above, or send a recipe's ingredients here."}</p>
          </div>
        )}
        {SECTIONS.map((s) => {
          const rows = open.filter((i) => i.section === s.key);
          if (!rows.length) return null;
          return (
            <section key={s.key}>
              <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">{s.label}<span className="rounded-full bg-surface-raised px-2 py-0.5 text-[11px] normal-case tracking-normal">{rows.length}</span></h3>
              <ul className="overflow-hidden rounded-lg bg-surface shadow-soft ring-1 ring-line/70">
                {rows.map((i) => <ItemRow key={i.id} item={i} onTick={() => upd.mutate({ id: i.id, patch: { checked: true } })} onEdit={() => setEditing(i)} />)}
              </ul>
            </section>
          );
        })}

        {ticked.length > 0 && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">In the trolley · {ticked.length}</h3>
              <button type="button" onClick={() => clear.mutate()} disabled={clear.isPending} className="text-sm font-semibold text-primary">Clear ticked</button>
            </div>
            <ul className="overflow-hidden rounded-lg bg-surface/60 ring-1 ring-line/70">
              {ticked.map((i) => <ItemRow key={i.id} item={i} onTick={() => upd.mutate({ id: i.id, patch: { checked: false } })} onEdit={() => setEditing(i)} />)}
            </ul>
          </section>
        )}
      </div>

      {/* Side: buy again */}
      <aside className="min-w-0 space-y-4">
        <div className="rounded-lg bg-surface p-4 shadow-soft ring-1 ring-line/70">
          <h3 className="mb-1 font-semibold">Buy again</h3>
          <p className="mb-3 text-xs text-ink-muted">What we get most. One tap puts it back on.</p>
          {chips.length === 0 ? <p className="text-sm text-ink-muted">Fills up as you use the list.</p> : (
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <button key={c.name} type="button" onClick={() => add.mutate([{ name: c.name, section: c.section }])}
                  className="inline-flex items-center gap-1 rounded-full bg-surface-raised px-3 py-1.5 text-sm hover:bg-primary/10 hover:text-primary">
                  <Plus className="h-3.5 w-3.5" />{c.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <Link to="/kitchen/recipes" className="flex items-center gap-3 rounded-lg bg-panel p-4 text-panel-ink shadow-soft">
          <ChefHat className="h-6 w-6 shrink-0" />
          <span className="text-sm"><span className="block font-semibold">Cooking something?</span>Open a recipe and send its ingredients here.</span>
        </Link>
        <p className="px-1 text-xs text-ink-muted">The list refreshes on both phones every few seconds while it's open.</p>
      </aside>

      {editing && <ItemSheet item={editing} onClose={() => setEditing(null)} />}
      {tripSheet && <TaskSheet task={trip ?? undefined} seed={{ title: "Go to the shops", kind: "shopping", due_on: ymd(new Date()) }} listCount={open.length} onClose={() => setTripSheet(false)} />}
    </div>
  );
}

/** The "Go to the shops" task, right on top of the list (028). */
function TripBanner({ trip, people, toGet, inTrolley, onPlan, onDone, busy }: { trip: Task | null; people: { id: string; display_name: string; colour: string }[]; toGet: number; inTrolley: number; onPlan: () => void; onDone: () => void; busy: boolean }) {
  if (!trip) {
    return (
      <button type="button" onClick={onPlan} className="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-line p-4 text-left transition hover:border-primary/50 hover:bg-primary/5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><ShoppingCart className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1"><span className="block font-semibold">Who's doing the shop?</span><span className="text-sm text-ink-muted">Make it a task: pick who and when. It shows on Home with a link back here.</span></span>
        <ChevronRight className="h-5 w-5 shrink-0 text-ink-muted" />
      </button>
    );
  }
  const p = people.find((x) => x.id === trip.assignee_id);
  const today = ymd(new Date());
  const when = !trip.due_on ? "" : trip.due_on === today ? " today" : trip.due_on < today ? ` (was ${format(parseISO(trip.due_on), "EEE d MMM")})` : ` ${format(parseISO(trip.due_on), "EEE d MMM")}`;
  const colour = tokenColour(p?.colour ?? "primary");
  return (
    <div className="overflow-hidden rounded-lg text-white shadow-soft" style={{ background: colour }}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20"><ShoppingCart className="h-5 w-5" /></span>
        <button type="button" onClick={onPlan} className="min-w-0 flex-1 text-left">
          <span className="block font-semibold">{p ? `${p.display_name}'s doing the shop` : "Shopping trip on"}{when}</span>
          <span className="text-sm text-white/85">{toGet} to get · {inTrolley} in the trolley{trip.note ? ` · ${trip.note}` : ""}</span>
        </button>
        <button type="button" onClick={onDone} disabled={busy} className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold disabled:opacity-60" style={{ color: colour }}><Check className="h-4 w-4" />Done shopping</button>
      </div>
      {(toGet + inTrolley) > 0 && <div className="h-1.5 bg-white/20"><div className="h-full bg-white/80 transition-all" style={{ width: `${Math.round((inTrolley / (toGet + inTrolley)) * 100)}%` }} /></div>}
    </div>
  );
}

function ItemRow({ item, onTick, onEdit }: { item: ShopItem; onTick: () => void; onEdit: () => void }) {
  const done = !!item.checked_at;
  return (
    <li className="flex items-center gap-3 border-b border-line/60 px-3 py-2.5 last:border-0">
      <button type="button" onClick={onTick} aria-label={done ? "Put back on the list" : "Tick off"}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition ${done ? "border-good bg-good text-white" : "border-line hover:border-primary"}`}>
        {done && <Check className="h-4 w-4" />}
      </button>
      <button type="button" onClick={onEdit} className="min-w-0 flex-1 text-left">
        <span className={`block truncate ${done ? "text-ink-muted line-through" : ""}`}>{item.name}</span>
        {(item.qty || item.note || item.recipe_id) && (
          <span className="block truncate text-xs text-ink-muted">{[item.qty, item.note, item.recipe_id ? "for a recipe" : null].filter(Boolean).join(" · ")}</span>
        )}
      </button>
    </li>
  );
}

function ItemSheet({ item, onClose }: { item: ShopItem; onClose: () => void }) {
  const upd = useUpdateShopping();
  const [name, setName] = useState(item.name); const [qty, setQty] = useState(item.qty ?? "");
  const [section, setSection] = useState<Section>(item.section); const [note, setNote] = useState(item.note ?? "");
  const save = () => { upd.mutate({ id: item.id, patch: { name: name.trim() || item.name, qty: qty.trim() || null, section, note: note.trim() || null } }); onClose(); };
  return (
    <Sheet title="Edit item" onClose={onClose}>
      <div className="space-y-4">
        <Lbl label="What"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></Lbl>
        <Lbl label="How much"><input className={inputCls} value={qty} onChange={(e) => setQty(e.target.value)} placeholder="e.g. 2, 500 g, 1 bag" /></Lbl>
        <Lbl label="Section">
          <select className={inputCls} value={section} onChange={(e) => setSection(e.target.value as Section)}>{SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select>
        </Lbl>
        <Lbl label="Note" hint="Brand, size, 'only if on special'…"><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} /></Lbl>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={save} className={`${primaryBtn} flex-1 py-3`}>Save</button>
          <button type="button" onClick={() => { upd.mutate({ id: item.id, patch: { remove: true } }); onClose(); }} className={`${quietBtn} py-3 text-bad`}><Trash2 className="h-4 w-4" /> Remove</button>
        </div>
      </div>
    </Sheet>
  );
}

// ==========================================================================
// Recipe book
// ==========================================================================
const mins = (n: number | null) => (n === null ? null : n >= 60 ? `${Math.floor(n / 60)} h${n % 60 ? ` ${n % 60} min` : ""}` : `${n} min`);
const totalMins = (r: Pick<Recipe, "prep_mins" | "cook_mins">) => (r.prep_mins ?? 0) + (r.cook_mins ?? 0) || null;
const SOURCE = { link: { icon: Globe, label: "From a website", tone: "bg-chart-4/15 text-chart-4" }, photo: { icon: ImageIcon, label: "From a photo", tone: "bg-chart-2/15 text-chart-2" }, typed: { icon: FileText, label: "Typed in", tone: "bg-chart-5/15 text-chart-5" } } as const;

// The recipe book as a binder (second pass, 10 Oct). James: "make this like a
// book with tabs running down the side … snaz this up a bit", then "looks a bit
// compressed and spacing is off … a little underwhelming".
// One bold idea: divider tabs. Each tab is a solid colour; the open tab is the
// same colour as the divider band across the top of the page and the page's
// edge, so tab, edge and band read as one sheet. Everything else stays quiet.
// Desktop: tabs down the right edge. Phone: tabs across the top, joined to the band.
type TabKey = "all" | "fav" | string;
type IndexTab = { key: TabKey; name: string; colour: string; icon?: typeof Heart; n: number };

function RecipeBook() {
  const recipes = useRecipes(); const tabs = useRecipeTabs();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"az" | "new" | "quick">("az");
  const [view, setViewRaw] = useState<"cards" | "list">(() => { try { return localStorage.getItem("recipes.view") === "list" ? "list" : "cards"; } catch { return "cards"; } });
  const setView = (v: "cards" | "list") => { setViewRaw(v); try { localStorage.setItem("recipes.view", v); } catch { /* private mode */ } };
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<RecipeDraft | null>(null);
  const [tabSheet, setTabSheet] = useState<RecipeTab | "new" | null>(null);
  const openId = params.get("open"); const tabKey: TabKey = params.get("tab") ?? "all";
  const all = recipes.data ?? []; const tabList = tabs.data ?? [];
  const open = all.find((r) => r.id === openId) ?? null;
  const active = tabList.find((t) => t.id === tabKey) ?? null;
  const setOpen = (id: string | null) => { const p = new URLSearchParams(params); if (id) p.set("open", id); else p.delete("open"); setParams(p, { replace: true }); };
  const pickTab = (k: TabKey) => { const p = new URLSearchParams(params); if (k === "all") p.delete("tab"); else p.set("tab", k); p.delete("open"); setParams(p, { replace: true }); };

  const inTab = (r: Recipe, k: TabKey) => k === "all" || (k === "fav" ? r.favourite : !!tabList.find((t) => t.id === k && hasTab(r, t)));
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return all.filter((r) => inTab(r, tabKey) && (!t || r.title.toLowerCase().includes(t) || r.tags.some((g) => g.toLowerCase().includes(t)) || r.ingredients.some((i) => i.item.toLowerCase().includes(t))))
      .sort((a, b) => sort === "new" ? b.created_at.localeCompare(a.created_at)
        : sort === "quick" ? (totalMins(a) ?? 1e9) - (totalMins(b) ?? 1e9) || a.title.localeCompare(b.title)
        : Number(b.favourite) - Number(a.favourite) || a.title.localeCompare(b.title));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, tabList, tabKey, q, sort]);
  // Cards shrink as the book fills up: big with a few, tighter with lots; or a plain list.
  const size: CardSize = shown.length <= 4 ? "lg" : shown.length <= 12 ? "md" : "sm";

  const index: IndexTab[] = [
    { key: "all", name: "All recipes", colour: "primary", icon: BookOpen, n: all.length },
    { key: "fav", name: "Favourites", colour: "chart-6", icon: Heart, n: all.filter((r) => r.favourite).length },
    ...tabList.map((t) => ({ key: t.id, name: t.name, colour: t.colour as string, n: all.filter((r) => hasTab(r, t)).length })),
  ];
  const cur = index.find((t) => t.key === tabKey) ?? index[0];
  const ink = tokenColour(cur.colour);
  const blurb = tabKey === "all" ? " Everything in the book, favourites first." : tabKey === "fav" ? " The ones we come back to." : "";
  // Phone: keep the open tab in view in the scrolling tab strip.
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(`[data-booktab="${tabKey}"]`);
    if (el && el.offsetParent) el.parentElement?.scrollTo({ left: el.offsetLeft - 40, behavior: "smooth" });
  }, [tabKey, index.length]);
  const withTab = (d: RecipeDraft): RecipeDraft => (active && !hasTab(d, active) ? { ...d, tags: [...d.tags, active.name] } : d);

  return (
    <>
      {/* Phone: tabs across the top; the open one runs straight into the band */}
      <div className="-mx-4 flex items-end gap-1.5 overflow-x-auto px-4 lg:hidden">
        {index.map((t) => {
          const on = t.key === tabKey;
          return (
            <button key={t.key} type="button" onClick={() => pickTab(t.key)} data-booktab={t.key}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-xl px-4 text-sm font-semibold transition-all ${on ? "h-11 text-white" : "h-9 text-ink"}`}
              style={{ background: tokenColour(t.colour, on ? 1 : 0.16) }}>
              {t.icon && <t.icon className="h-4 w-4" />}{t.key === "all" ? "All" : t.name}
            </button>
          );
        })}
        <button type="button" onClick={() => setTabSheet("new")} aria-label="New tab" className="flex h-9 shrink-0 items-center rounded-t-xl border-2 border-b-0 border-dashed border-line px-3 text-ink-muted"><Plus className="h-4 w-4" /></button>
      </div>

      <div className="flex items-stretch">
        {/* The page */}
        <div className="min-w-0 flex-1 overflow-hidden rounded-b-2xl bg-surface shadow-soft ring-1 ring-line/60 lg:rounded-2xl lg:rounded-tr-none lg:border-r-[6px] lg:ring-0" style={{ borderColor: ink }}>
          {/* Divider band */}
          <div className="relative overflow-hidden px-5 pb-6 pt-6 text-white sm:px-8 lg:px-10 lg:pb-9 lg:pt-10" style={{ background: `linear-gradient(115deg, ${tokenColour(cur.colour)} 0%, ${tokenColour(cur.colour, 0.9)} 45%, ${tokenColour(cur.colour, 0.72)} 100%)` }}>
            {cur.icon ? <cur.icon className="pointer-events-none absolute -right-6 -top-6 hidden h-48 w-48 opacity-[0.08] sm:block" strokeWidth={1.25} /> : <ChefHat className="pointer-events-none absolute -right-6 -top-6 hidden h-48 w-48 opacity-[0.08] sm:block" strokeWidth={1.25} />}
            <div className="relative flex flex-wrap items-end gap-x-8 gap-y-5">
              <div className="min-w-0 flex-1">
                <h2 className="flex items-center gap-3 font-heading text-[34px] font-semibold leading-none sm:text-5xl">
                  {cur.name}
                  {active && <button type="button" onClick={() => setTabSheet(active)} aria-label={`Rename or recolour ${active.name}`} className="rounded-full p-2 text-white/70 hover:bg-white/15 hover:text-white"><Pencil className="h-5 w-5" /></button>}
                </h2>
                <p className="mt-3 hidden text-[15px] text-white/85 sm:block">{cur.n} {cur.n === 1 ? "recipe" : "recipes"}.{blurb}</p>
              </div>
              <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
                <div className="relative min-w-0 basis-full sm:w-72 sm:basis-auto">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/70" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a recipe or ingredient" aria-label="Search recipes"
                    className="w-full rounded-full border border-white/25 bg-white/15 py-2.5 pl-10 pr-4 text-[15px] text-white outline-none placeholder:text-white/65 focus:border-white/70 focus:bg-white/20" />
                </div>
                <button type="button" onClick={() => setAdding(true)} className="inline-flex flex-1 shrink-0 items-center justify-center gap-1.5 rounded-full bg-white px-5 sm:flex-none py-2.5 text-[15px] font-semibold shadow-sm transition hover:shadow-md" style={{ color: ink }}>
                  <Plus className="h-4 w-4" /> Add recipe
                </button>
              </div>
            </div>
          </div>

          {/* Recipes */}
          <div className="p-5 sm:p-8 lg:p-10">
            {all.length > 1 && (
              <div className="mb-6 flex items-center justify-between gap-3">
                <div className="flex rounded-full bg-surface-raised p-1 text-sm" role="group" aria-label="Sort recipes">
                  {([["az", "A to Z"], ["new", "Newest"], ["quick", "Quickest"]] as const).map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setSort(k)} aria-pressed={sort === k}
                      className={`whitespace-nowrap rounded-full px-2.5 py-1.5 text-[13px] transition sm:px-3.5 sm:text-sm ${sort === k ? "bg-surface font-semibold text-ink shadow-sm" : "text-ink-muted hover:text-ink"}`}>{l}</button>
                  ))}
                </div>
                <div className="flex rounded-full bg-surface-raised p-1" role="group" aria-label="Show as">
                  <button type="button" onClick={() => setView("cards")} aria-pressed={view === "cards"} aria-label="Cards" className={`rounded-full p-2 transition ${view === "cards" ? "bg-surface text-ink shadow-sm" : "text-ink-muted hover:text-ink"}`}><LayoutGrid className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setView("list")} aria-pressed={view === "list"} aria-label="List" className={`rounded-full p-2 transition ${view === "list" ? "bg-surface text-ink shadow-sm" : "text-ink-muted hover:text-ink"}`}><List className="h-4 w-4" /></button>
                </div>
              </div>
            )}
            {recipes.isLoading && <p className="text-sm text-ink-muted">Opening the book…</p>}
            {!recipes.isLoading && q && shown.length === 0 && <p className="py-10 text-center text-ink-muted">Nothing matches “{q}”.</p>}
            {!recipes.isLoading && view === "list" && shown.length > 0 && (
              <ul className="overflow-hidden rounded-xl ring-1 ring-line">
                {shown.map((r) => <RecipeRow key={r.id} r={r} tabs={tabList} onOpen={() => setOpen(r.id)} />)}
                {!q && tabKey !== "fav" && (
                  <li><button type="button" onClick={() => setAdding(true)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-semibold text-ink-muted hover:bg-surface-raised hover:text-ink">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: tokenColour(cur.colour, 0.12), color: ink }}><Plus className="h-4 w-4" /></span>{active ? `Add a recipe to ${active.name}` : "Add a recipe"}
                  </button></li>
                )}
              </ul>
            )}
            {!recipes.isLoading && (view === "cards" || shown.length === 0) && !(q && shown.length === 0) && (
              <div className={`grid ${GRID[size]}`}>
                {shown.map((r) => <RecipeCard key={r.id} r={r} tabs={tabList} size={size} onOpen={() => setOpen(r.id)} />)}
                {!q && tabKey !== "fav" && (
                  <button type="button" onClick={() => setAdding(true)}
                    className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-line p-6 text-center text-ink-muted transition hover:border-current hover:text-ink ${size === "lg" ? "min-h-[300px]" : size === "md" ? "min-h-[230px]" : "min-h-[170px]"}`}
                    style={{ color: shown.length ? undefined : ink }}>
                    <span className={`flex items-center justify-center rounded-full ${size === "sm" ? "h-10 w-10" : "h-14 w-14"}`} style={{ background: tokenColour(cur.colour, 0.12), color: ink }}><Plus className="h-6 w-6" /></span>
                    <span className="font-semibold">{active ? `Add a recipe to ${active.name}` : "Add a recipe"}</span>
                    {size === "lg" && <span className="max-w-[220px] text-sm text-ink-muted">From a website link, a photo or screenshot, or type it in.</span>}
                  </button>
                )}
                {tabKey === "fav" && shown.length === 0 && <p className="col-span-full py-10 text-center text-ink-muted">No favourites yet. Open a recipe and tap the heart.</p>}
              </div>
            )}
            {!recipes.isLoading && !q && active && shown.length === 0 && all.length > 0 && (
              <p className="mt-6 text-sm text-ink-muted">Already have one that belongs here? Open it and tap <b>{active.name}</b> under “Filed under”.</p>
            )}
          </div>
        </div>

        {/* Desktop: divider tabs down the right edge */}
        <nav className="hidden w-48 shrink-0 flex-col gap-2 lg:flex" aria-label="Recipe book tabs">
          {index.map((t) => {
            const on = t.key === tabKey;
            return (
              <button key={t.key} type="button" onClick={() => pickTab(t.key)} aria-current={on ? "page" : undefined}
                className={`flex items-center gap-2.5 rounded-r-xl py-3.5 pl-4 pr-4 text-left text-[15px] font-semibold transition-all ${on ? "w-full text-white shadow-soft" : "w-[88%] text-ink hover:w-[94%]"}`}
                style={{ background: tokenColour(t.colour, on ? 1 : 0.16) }}>
                {t.icon ? <t.icon className={`h-4 w-4 shrink-0 ${on && t.key === "fav" ? "fill-current" : ""}`} /> : <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: on ? "white" : tokenColour(t.colour) }} />}
                <span className="min-w-0 flex-1 truncate">{t.name}</span>
                <span className={`num text-sm ${on ? "text-white/80" : "font-normal text-ink-muted"}`}>{t.n}</span>
              </button>
            );
          })}
          <button type="button" onClick={() => setTabSheet("new")} className="mt-2 flex w-[88%] items-center gap-2.5 rounded-r-xl border-2 border-l-0 border-dashed border-line py-3 pl-4 pr-4 text-[15px] text-ink-muted transition hover:text-ink">
            <Plus className="h-4 w-4" /> New tab
          </button>
        </nav>
      </div>

      {adding && <AddRecipe onClose={() => setAdding(false)} onDraft={(d) => { setAdding(false); setDraft(withTab(d)); }} onPhoto={(id) => { setAdding(false); setOpen(id); }} />}
      {draft && <RecipeEditor draft={draft} tabs={tabList} onClose={() => setDraft(null)} onSaved={(id) => { setDraft(null); setOpen(id); }} />}
      {open && !draft && <RecipeDetail r={open} tabs={tabList} onClose={() => setOpen(null)} onEdit={(d) => setDraft(d)} />}
      {tabSheet && <TabSheet tab={tabSheet === "new" ? null : tabSheet} count={tabList.length} recipes={all} onClose={() => setTabSheet(null)} onRemoved={() => pickTab("all")} />}
    </>
  );
}

type CardSize = "lg" | "md" | "sm";
const GRID: Record<CardSize, string> = {
  lg: "gap-6 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]",
  md: "grid-cols-2 gap-3 sm:gap-5 sm:[grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]",
  sm: "grid-cols-2 gap-3 sm:gap-4 sm:[grid-template-columns:repeat(auto-fill,minmax(160px,1fr))]",
};

function RecipeCard({ r, tabs, size, onOpen }: { r: Recipe; tabs: RecipeTab[]; size: CardSize; onOpen: () => void }) {
  const photo = useWorkerFile(r.uploaded && r.mime?.startsWith("image/") ? `/recipes/${r.id}/file` : null);
  const t = totalMins(r);
  const mine = tabs.filter((x) => hasTab(r, x));
  const colour = mine[0]?.colour ?? "primary";
  const S = SOURCE[r.source_kind];
  const art = size === "lg" ? "h-40" : size === "md" ? "h-28" : "h-20";
  return (
    <button type="button" onClick={onOpen} className={`group flex min-w-0 flex-col overflow-hidden rounded-xl bg-surface text-left ring-1 ring-line transition hover:shadow-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${size === "lg" ? "min-h-[300px]" : ""}`}>
      <div className={`relative ${art} overflow-hidden`} style={{ background: tokenColour(colour, 0.12) }}>
        {photo.url
          ? <img src={photo.url} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
          : <span className={`absolute inset-0 flex items-center justify-center font-heading font-semibold ${size === "lg" ? "text-7xl" : size === "md" ? "text-5xl" : "text-4xl"}`} style={{ color: tokenColour(colour, 0.55) }}>{r.title.trim().charAt(0).toUpperCase()}</span>}
        {r.favourite && <span className={`absolute right-2.5 top-2.5 flex items-center justify-center rounded-full bg-surface shadow-sm ${size === "sm" ? "h-6 w-6" : "h-8 w-8"}`}><Heart className={`${size === "sm" ? "h-3 w-3" : "h-4 w-4"} fill-primary text-primary`} /></span>}
        {t && size !== "sm" && <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 rounded-full bg-surface/95 px-2.5 py-1 text-xs font-semibold text-ink shadow-sm"><Clock className="h-3.5 w-3.5" />{mins(t)}</span>}
      </div>
      <div className={`flex flex-1 flex-col ${size === "lg" ? "gap-3 p-5" : size === "md" ? "gap-2 p-4" : "gap-1.5 p-3"}`}>
        <div className={`line-clamp-2 font-heading font-semibold leading-snug text-ink ${size === "lg" ? "text-xl" : size === "md" ? "text-[17px]" : "text-[15px]"}`}>{r.title}</div>
        {mine.length > 0 && (size === "sm"
          ? <div className="flex gap-1">{mine.map((x) => <span key={x.id} title={x.name} className="h-2 w-2 rounded-full" style={{ background: tokenColour(x.colour) }} />)}</div>
          : <div className="flex flex-wrap gap-1.5">
              {mine.map((x) => <span key={x.id} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold text-ink" style={{ background: tokenColour(x.colour, 0.14) }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: tokenColour(x.colour) }} />{x.name}</span>)}
            </div>)}
        <div className={`mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-ink-muted ${size === "sm" ? "text-xs" : "text-[13px]"}`}>
          {size === "sm" && t && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{mins(t)}</span>}
          {r.servings && <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" />{size === "lg" ? `Serves ${r.servings}` : r.servings}</span>}
          {size === "lg" && r.ingredients.length > 0 && <span>{r.ingredients.length} ingredients</span>}
          {r.source_kind === "photo" && r.ingredients.length === 0
            ? <span className="inline-flex items-center gap-1.5 text-warn"><ScanText className="h-4 w-4" />{size === "sm" ? "" : "Photo only"}</span>
            : size === "lg" && <span className="inline-flex items-center gap-1.5"><S.icon className="h-4 w-4" />{r.source_kind === "link" && r.source_url ? new URL(r.source_url).hostname.replace(/^www\./, "") : S.label}</span>}
        </div>
      </div>
    </button>
  );
}

/** List view: one line per recipe, for scanning a full book. */
function RecipeRow({ r, tabs, onOpen }: { r: Recipe; tabs: RecipeTab[]; onOpen: () => void }) {
  const mine = tabs.filter((x) => hasTab(r, x)); const t = totalMins(r);
  const colour = mine[0]?.colour ?? "primary";
  return (
    <li className="border-b border-line/70 last:border-0">
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-surface-raised/70">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-lg font-semibold" style={{ background: tokenColour(colour, 0.14), color: tokenColour(colour) }}>{r.title.trim().charAt(0).toUpperCase()}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-ink">{r.title}</span>
          {mine.length > 0 && <span className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-muted">{mine.map((x) => <span key={x.id} className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: tokenColour(x.colour) }} />{x.name}</span>)}</span>}
        </span>
        {r.favourite && <Heart className="h-4 w-4 shrink-0 fill-primary text-primary" />}
        <span className="hidden w-20 shrink-0 text-right text-sm text-ink-muted sm:block">{t ? mins(t) : ""}</span>
        <span className="hidden w-16 shrink-0 text-right text-sm text-ink-muted md:block">{r.servings ? `Serves ${r.servings}` : ""}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
      </button>
    </li>
  );
}

function TabSheet({ tab, count, recipes, onClose, onRemoved }: { tab: RecipeTab | null; count: number; recipes: Recipe[]; onClose: () => void; onRemoved: () => void }) {
  const save = useSaveTab(); const archive = useArchiveTab();
  const [name, setName] = useState(tab?.name ?? ""); const [colour, setColour] = useState<TabColour>(tab?.colour ?? TAB_COLOURS[count % TAB_COLOURS.length]);
  const [err, setErr] = useState<string | null>(null); const [sure, setSure] = useState(false);
  const n = tab ? recipes.filter((r) => hasTab(r, tab)).length : 0;
  const submit = () => {
    if (!name.trim()) { setErr("Give the tab a name."); return; }
    save.mutate({ tab: { id: tab?.id, name, colour, sort_order: tab?.sort_order ?? (count + 1) * 10 + 100 }, oldName: tab?.name, recipes }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  return (
    <Sheet title={tab ? "Edit tab" : "New tab"} onClose={onClose}>
      <div className="space-y-5">
        <Lbl label="Name" hint="e.g. Soups, Slow cooker, Isadora, Quick weeknight"><input className={inputCls} value={name} maxLength={30} onChange={(e) => setName(e.target.value)} autoFocus /></Lbl>
        <div>
          <span className="text-sm font-medium text-ink-muted">Colour</span>
          <div className="mt-2 flex gap-2.5">
            {TAB_COLOURS.map((c) => (
              <button key={c} type="button" onClick={() => setColour(c)} aria-label={c}
                className={`flex h-10 w-10 items-center justify-center rounded-full transition ${colour === c ? "ring-2 ring-ink ring-offset-2 ring-offset-surface" : ""}`} style={{ background: tokenColour(c) }}>
                {colour === c && <Check className="h-4 w-4 text-white" />}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-surface-raised p-3 text-sm">
          <span className="rounded-r-lg py-1.5 pl-3 pr-4 font-semibold text-white" style={{ background: tokenColour(colour) }}>{name.trim() || "Tab"}</span>
          <span className="text-ink-muted">how it looks</span>
        </div>
        {tab && n > 0 && <p className="text-xs text-ink-muted">{n} {n === 1 ? "recipe is" : "recipes are"} under this tab. Renaming moves them with it.</p>}
        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={submit} disabled={save.isPending} className={`${primaryBtn} flex-1 py-3`}>{save.isPending ? "Saving…" : tab ? "Save" : "Add the tab"}</button>
          {tab && (sure
            ? <button type="button" onClick={() => archive.mutate(tab.id, { onSuccess: () => { onClose(); onRemoved(); } })} className={`${quietBtn} bg-bad/10 py-3 text-bad`}>Yes, take it out</button>
            : <button type="button" onClick={() => setSure(true)} className={`${quietBtn} py-3 text-bad`}><Trash2 className="h-4 w-4" />Remove</button>)}
        </div>
        {tab && sure && <p className="text-xs text-ink-muted">The recipes stay in the book; only the tab goes.</p>}
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------
// Add: three ways in
// --------------------------------------------------------------------------
const CLAUDE_PROMPT = `Write this recipe as Markdown in exactly this layout so I can paste it into our recipe book:

${MD_TEMPLATE}`;

function blankDraft(over: Partial<RecipeDraft> = {}): RecipeDraft {
  return { title: "", source_kind: "typed", source_url: null, servings: null, prep_mins: null, cook_mins: null, ingredients: [], method: [], notes: null, tags: [], favourite: false, ...over };
}

function AddRecipe({ onClose, onDraft, onPhoto }: { onClose: () => void; onDraft: (d: RecipeDraft) => void; onPhoto: (id: string) => void }) {
  const [way, setWay] = useState<"link" | "photo" | "typed" | null>(null);
  const [url, setUrl] = useState(""); const [md, setMd] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null); const [copied, setCopied] = useState(false);
  const file = useRef<HTMLInputElement>(null); const camera = useRef<HTMLInputElement>(null);
  const addPhoto = useAddRecipePhoto();

  const fromLink = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await importRecipe(url.trim());
      onDraft(blankDraft({
        title: r.title, source_kind: "link", source_url: r.source_url, servings: r.servings, prep_mins: r.prep_mins, cook_mins: r.cook_mins,
        ingredients: r.ingredients.map((l) => { const s = splitIngredient(l); return { ...s, section: guessSection(s.item) }; }), method: r.method, notes: r.notes,
      }));
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const fromPhoto = (f: File | undefined) => { if (!f) return; setErr(null); addPhoto.mutate(f, { onSuccess: onPhoto, onError: (e) => setErr(e.message) }); };
  const fromText = () => {
    const p = parseMarkdown(md);
    const looksMd = /^#|\n#/.test(md);
    // Plain text with no headings: first line is the title, the rest goes to notes for tidying in the editor.
    onDraft(looksMd ? blankDraft({ ...p, source_kind: "typed" }) : blankDraft({ title: md.split(/\r?\n/)[0]?.trim() || "", notes: md.split(/\r?\n/).slice(1).join("\n").trim() || null }));
  };
  const copyPrompt = async () => { try { await navigator.clipboard.writeText(CLAUDE_PROMPT); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ } };

  const Way = ({ k, icon: Icon, title, sub }: { k: "link" | "photo" | "typed"; icon: typeof Globe; title: string; sub: string }) => (
    <button type="button" onClick={() => { setWay(k); setErr(null); }} className={`flex w-full items-center gap-4 rounded-xl p-4 text-left transition ${way === k ? "bg-primary/10 ring-2 ring-primary/40" : "bg-surface-raised hover:bg-line/50"}`}>
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${SOURCE[k].tone}`}><Icon className="h-5 w-5" /></span>
      <span className="min-w-0"><span className="block font-semibold">{title}</span><span className="text-sm text-ink-muted">{sub}</span></span>
    </button>
  );

  return (
    <Sheet title="Add a recipe" onClose={onClose}>
      <div className="space-y-2">
        <Way k="link" icon={Globe} title="From a website" sub="Paste the link. Most recipe sites share the recipe in a standard way." />
        {way === "link" && (
          <form onSubmit={(e) => { e.preventDefault(); void fromLink(); }} className="space-y-2 rounded-xl bg-surface-raised/50 p-3">
            <input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.taste.com.au/recipes/…" inputMode="url" autoFocus />
            <button type="submit" disabled={busy || !/^https?:\/\/\S+\.\S+/.test(url.trim())} className={`${primaryBtn} w-full py-2.5`}>{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading the page…</> : "Get the recipe"}</button>
            <p className="text-xs text-ink-muted">No AI: the app reads the recipe the page publishes for search engines. You check it before it's saved.</p>
          </form>
        )}

        <Way k="photo" icon={Camera} title="Photo or screenshot" sub="A cookbook page, a screenshot from Instagram, Mum's handwritten card." />
        {way === "photo" && (
          <div className="space-y-2 rounded-xl bg-surface-raised/50 p-3">
            <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { fromPhoto(e.target.files?.[0]); e.target.value = ""; }} />
            <input ref={file} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { fromPhoto(e.target.files?.[0]); e.target.value = ""; }} />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => camera.current?.click()} disabled={addPhoto.isPending} className={`${primaryBtn} py-2.5`}><Camera className="h-4 w-4" /> Take photo</button>
              <button type="button" onClick={() => file.current?.click()} disabled={addPhoto.isPending} className={`${quietBtn} py-2.5`}><ImageIcon className="h-4 w-4" /> Choose file</button>
            </div>
            {addPhoto.isPending && <p className="flex items-center gap-2 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</p>}
            <p className="text-xs text-ink-muted">It's saved straight away as a photo. Press "Read it" on the recipe if you want the reader to copy out the ingredients and method (one AI call, only when you ask).</p>
          </div>
        )}

        <Way k="typed" icon={FileText} title="Type or paste" sub="Write it yourself, or paste Markdown you made with Claude." />
        {way === "typed" && (
          <div className="space-y-2 rounded-xl bg-surface-raised/50 p-3">
            <textarea className={`${inputCls} min-h-[220px] font-mono text-sm`} value={md} onChange={(e) => setMd(e.target.value)} placeholder={MD_TEMPLATE} autoFocus />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={fromText} disabled={!md.trim()} className={`${primaryBtn} flex-1 py-2.5`}>Next: check it</button>
              <button type="button" onClick={() => setMd(MD_TEMPLATE)} className={`${quietBtn} py-2.5`}>Use the template</button>
            </div>
            <button type="button" onClick={copyPrompt} className="flex w-full items-start gap-2 rounded-lg bg-surface p-3 text-left text-xs text-ink-muted ring-1 ring-line/70 hover:text-ink">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{copied ? <b className="text-good">Copied. Paste it into Claude with your recipe, then paste Claude's answer here.</b> : <>Making one with Claude? <b>Copy the instructions</b> to paste into the chat so it comes back in this exact layout.</>}</span>
            </button>
          </div>
        )}
        <ErrorText>{err}</ErrorText>
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------
// Edit / check before saving
// --------------------------------------------------------------------------
const ingText = (xs: Ingredient[]) => xs.map((i) => `${i.qty ? `${i.qty} ` : ""}${i.item}`).join("\n");
const numOrNull = (s: string) => { const n = Number(s); return s.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n) : null; };

function RecipeEditor({ draft, tabs, onClose, onSaved }: { draft: RecipeDraft; tabs: RecipeTab[]; onClose: () => void; onSaved: (id: string) => void }) {
  const save = useSaveRecipe();
  const [title, setTitle] = useState(draft.title);
  const [servings, setServings] = useState(draft.servings?.toString() ?? ""); const [prep, setPrep] = useState(draft.prep_mins?.toString() ?? ""); const [cook, setCook] = useState(draft.cook_mins?.toString() ?? "");
  const [ings, setIngs] = useState(ingText(draft.ingredients)); const [method, setMethod] = useState(draft.method.join("\n"));
  const [notes, setNotes] = useState(draft.notes ?? ""); const isTab = (g: string) => tabs.some((t) => t.name.trim().toLowerCase() === g.trim().toLowerCase());
  const [onTabs, setOnTabs] = useState<string[]>(tabs.filter((t) => hasTab(draft, t)).map((t) => t.name));
  const [tags, setTags] = useState(draft.tags.filter((g) => !isTab(g)).join(", ")); const [url, setUrl] = useState(draft.source_url ?? "");
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    if (!title.trim()) { setErr("Give it a name."); return; }
    if (url.trim() && !/^https?:\/\//.test(url.trim())) { setErr("The link needs to start with http:// or https://"); return; }
    const sv = numOrNull(servings);
    if (sv !== null && (sv < 1 || sv > 100)) { setErr("Serves should be between 1 and 100."); return; }
    const ingredients = ings.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => { const s = splitIngredient(l); return { ...s, section: guessSection(s.item) }; });
    save.mutate({
      ...draft, title: title.trim(), servings: sv, prep_mins: numOrNull(prep), cook_mins: numOrNull(cook), ingredients,
      method: method.split(/\r?\n/).map((l) => l.replace(/^(\d+[.)]|[-*•])\s*/, "").trim()).filter(Boolean),
      notes: notes.trim() || null, tags: [...onTabs, ...tags.split(",").map((t) => t.trim()).filter((t) => t && !isTab(t))], source_url: url.trim() || null,
    }, { onSuccess: onSaved, onError: (e) => setErr(e.message) });
  };

  return (
    <Sheet title={draft.id ? "Edit recipe" : "Check and save"} onClose={onClose} wide>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-4">
          <Lbl label="Name"><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Spaghetti bolognese" /></Lbl>
          <div className="grid grid-cols-3 gap-3">
            <Lbl label="Serves"><input className={inputCls} inputMode="numeric" value={servings} onChange={(e) => setServings(e.target.value)} /></Lbl>
            <Lbl label="Prep (min)"><input className={inputCls} inputMode="numeric" value={prep} onChange={(e) => setPrep(e.target.value)} /></Lbl>
            <Lbl label="Cook (min)"><input className={inputCls} inputMode="numeric" value={cook} onChange={(e) => setCook(e.target.value)} /></Lbl>
          </div>
          <Lbl label="Ingredients" hint="One per line, amount first: 500 g beef mince"><textarea className={`${inputCls} min-h-[220px] text-sm`} value={ings} onChange={(e) => setIngs(e.target.value)} /></Lbl>
        </div>
        <div className="space-y-4">
          <Lbl label="Method" hint="One step per line"><textarea className={`${inputCls} min-h-[220px] text-sm`} value={method} onChange={(e) => setMethod(e.target.value)} /></Lbl>
          <Lbl label="Notes"><textarea className={`${inputCls} min-h-[80px] text-sm`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Swaps, what Isadora will eat, double it for the freezer…" /></Lbl>
          <div>
            <span className="text-sm font-medium text-ink-muted">Book tabs</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {tabs.map((t) => {
                const on = onTabs.includes(t.name);
                return (
                  <button key={t.id} type="button" onClick={() => setOnTabs(on ? onTabs.filter((x) => x !== t.name) : [...onTabs, t.name])}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold transition"
                    style={on ? { background: tokenColour(t.colour), color: "white" } : { background: tokenColour(t.colour, 0.12), color: tokenColour(t.colour) }}>
                    {on && <Check className="h-3.5 w-3.5" />}{t.name}
                  </button>
                );
              })}
              {tabs.length === 0 && <span className="text-sm text-ink-muted">No tabs yet. Add one from the side of the book.</span>}
            </div>
          </div>
          <Lbl label="Other tags" hint="Comma between them: dinner, quick, freezer"><input className={inputCls} value={tags} onChange={(e) => setTags(e.target.value)} /></Lbl>
          <Lbl label="Link (if it came from a website)"><input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} inputMode="url" /></Lbl>
        </div>
      </div>
      <ErrorText>{err}</ErrorText>
      <div className="mt-5 flex gap-2">
        <button type="button" onClick={submit} disabled={save.isPending} className={`${primaryBtn} flex-1 py-3`}>{save.isPending ? "Saving…" : "Save to the book"}</button>
        <button type="button" onClick={onClose} className={`${quietBtn} py-3`}>Cancel</button>
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------
// Open a recipe
// --------------------------------------------------------------------------
function download(name: string, text: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/markdown" }));
  a.download = `${name.replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "-").toLowerCase() || "recipe"}.md`;
  a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function RecipeDetail({ r, tabs, onClose, onEdit }: { r: Recipe; tabs: RecipeTab[]; onClose: () => void; onEdit: (d: RecipeDraft) => void }) {
  const nav = useNavigate();
  const save = useSaveRecipe(); const archive = useArchiveRecipe(); const add = useAddShopping(); const list = useShopping();
  const file = useWorkerFile(r.uploaded ? `/recipes/${r.id}/file` : null);
  const [serves, setServes] = useState(r.servings ?? 0);
  const [pick, setPick] = useState<Set<number>>(() => new Set(r.ingredients.map((_, i) => i)));
  const [reading, setReading] = useState(false); const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null); const [sure, setSure] = useState(false);
  const listReady = list.isSuccess;
  // Start with everything ticked except what's already on the list.
  useEffect(() => {
    const on = new Set((list.data ?? []).filter((i) => !i.checked_at).map((i) => i.name.trim().toLowerCase()));
    setPick(new Set(r.ingredients.flatMap((x, i) => (on.has(x.item.trim().toLowerCase()) ? [] : [i]))));
    setServes(r.servings ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.id, r.ingredients, r.servings, listReady]);

  const by = r.servings && serves ? serves / r.servings : 1;
  const already = new Set((list.data ?? []).filter((i) => !i.checked_at).map((i) => i.name.trim().toLowerCase()));
  const draftOf = (): RecipeDraft => ({ id: r.id, title: r.title, source_kind: r.source_kind, source_url: r.source_url, servings: r.servings, prep_mins: r.prep_mins, cook_mins: r.cook_mins, ingredients: r.ingredients, method: r.method, notes: r.notes, tags: r.tags, favourite: r.favourite });
  const flash = (t: string) => { setNote(t); setTimeout(() => setNote(null), 2500); };

  const toList = () => {
    const rows = r.ingredients.filter((_, i) => pick.has(i)).map((i) => ({ name: i.item, qty: scaleQty(i.qty, by), section: i.section ?? guessSection(i.item), recipe_id: r.id }));
    add.mutate(rows, { onSuccess: () => flash(`${rows.length} added to the shopping list`) });
  };
  const readIt = async () => {
    setReading(true); setErr(null);
    try {
      const s = await readRecipePhoto(r.id);
      onEdit({ ...draftOf(), title: r.title === "New recipe" ? s.title : r.title, servings: s.servings, prep_mins: s.prep_mins, cook_mins: s.cook_mins,
        ingredients: s.ingredients.map((i) => ({ qty: i.qty, item: i.item, section: guessSection(i.item) })), method: s.method, notes: s.notes ?? r.notes });
    } catch (e) { setErr((e as Error).message); } finally { setReading(false); }
  };
  const md = () => toMarkdown(r);
  const copy = async () => { try { await navigator.clipboard.writeText(md()); flash("Copied as Markdown"); } catch { setErr("Couldn't reach the clipboard. Use Download instead."); } };
  const S = SOURCE[r.source_kind];
  const iconBtn = "inline-flex items-center gap-1.5 rounded-full bg-surface-raised px-3 py-2 text-sm hover:bg-line/60";

  return (
    <Sheet title={r.title} onClose={onClose} wide>
      <div className="mb-5 flex flex-wrap items-center gap-2 text-sm">
        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${S.tone}`}><S.icon className="h-3.5 w-3.5" />{S.label}</span>
        {r.prep_mins !== null && <span className="text-ink-muted">Prep {mins(r.prep_mins)}</span>}
        {r.cook_mins !== null && <span className="text-ink-muted">· Cook {mins(r.cook_mins)}</span>}
        {r.tags.filter((g) => !tabs.some((t) => t.name.trim().toLowerCase() === g.trim().toLowerCase())).map((t) => <span key={t} className="rounded-full bg-surface-raised px-2.5 py-0.5 text-xs">{t}</span>)}
        {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline">{new URL(r.source_url).hostname.replace(/^www\./, "")}<ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>

      {tabs.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">Filed under</span>
          {tabs.map((t) => {
            const on = hasTab(r, t);
            return (
              <button key={t.id} type="button" onClick={() => save.mutate({ ...draftOf(), tags: on ? r.tags.filter((g) => g.trim().toLowerCase() !== t.name.trim().toLowerCase()) : [...r.tags, t.name] })}
                className="inline-flex items-center gap-1 rounded-r-lg rounded-l-sm px-2.5 py-1 text-xs font-semibold transition"
                style={on ? { background: tokenColour(t.colour), color: "white" } : { background: tokenColour(t.colour, 0.1), color: tokenColour(t.colour) }}>
                {on ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}{t.name}
              </button>
            );
          })}
        </div>
      )}
      <div className="-mx-6 mb-6 flex gap-2 overflow-x-auto px-6 pb-1 lg:mx-0 lg:flex-wrap lg:px-0 [&>button]:shrink-0 [&>button]:whitespace-nowrap">
        <button type="button" onClick={() => save.mutate({ ...draftOf(), favourite: !r.favourite })} className={`${iconBtn} ${r.favourite ? "text-primary" : ""}`}><Heart className={`h-4 w-4 ${r.favourite ? "fill-current" : ""}`} />Favourite</button>
        <button type="button" onClick={() => onEdit(draftOf())} className={iconBtn}><Pencil className="h-4 w-4" />Edit</button>
        <button type="button" onClick={copy} className={iconBtn}><ClipboardCopy className="h-4 w-4" />Copy as Markdown</button>
        <button type="button" onClick={() => download(r.title, md())} className={iconBtn}><Download className="h-4 w-4" />.md</button>
        {sure
          ? <button type="button" onClick={() => archive.mutate(r.id, { onSuccess: onClose })} className={`${iconBtn} bg-bad/10 text-bad`}><Trash2 className="h-4 w-4" />Yes, take it out</button>
          : <button type="button" onClick={() => setSure(true)} className={`${iconBtn} text-ink-muted`}><Trash2 className="h-4 w-4" />Remove</button>}
      </div>

      {r.source_kind === "photo" && r.ingredients.length === 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl bg-warn/10 p-4">
          <ScanText className="h-6 w-6 shrink-0 text-warn" />
          <div className="min-w-0 flex-1 text-sm"><b>Only the photo so far.</b> Read it to copy out the ingredients and method, or type them in with Edit.</div>
          <button type="button" onClick={readIt} disabled={reading || !r.uploaded} className={primaryBtn}>{reading ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading…</> : <><ScanText className="h-4 w-4" /> Read it</>}</button>
        </div>
      )}
      <ErrorText>{err}</ErrorText>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="min-w-0 space-y-4">
          {r.uploaded && (
            <div className="overflow-hidden rounded-lg bg-surface-raised">
              {file.url && r.mime?.startsWith("image/") && <a href={file.url} target="_blank" rel="noreferrer"><img src={file.url} alt="The recipe" className="max-h-80 w-full object-contain" /></a>}
              {file.url && r.mime === "application/pdf" && <a href={file.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-4 text-sm text-primary"><FileText className="h-5 w-5" />Open the PDF</a>}
              {!file.url && !file.err && <p className="p-4 text-sm text-ink-muted">Loading the photo…</p>}
              {file.err && <p className="p-4 text-sm text-ink-muted">The photo isn't reachable (is the Worker running?).</p>}
            </div>
          )}

          {r.ingredients.length > 0 && (
            <div className="rounded-lg bg-surface-raised/60 p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="font-semibold">Ingredients</h3>
                {r.servings ? (
                  <div className="flex items-center gap-1 text-sm">
                    <button type="button" aria-label="Fewer" onClick={() => setServes(Math.max(1, serves - 1))} className="rounded-full bg-surface p-1.5 ring-1 ring-line"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-20 text-center"><b className="num">{serves}</b> {serves === 1 ? "serve" : "serves"}</span>
                    <button type="button" aria-label="More" onClick={() => setServes(Math.min(100, serves + 1))} className="rounded-full bg-surface p-1.5 ring-1 ring-line"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                ) : null}
              </div>
              <ul className="space-y-1">
                {r.ingredients.map((i, n) => {
                  const on = pick.has(n); const have = already.has(i.item.trim().toLowerCase());
                  return (
                    <li key={n}>
                      <label className="flex cursor-pointer items-start gap-2.5 rounded-md px-1 py-1 hover:bg-surface">
                        <input type="checkbox" checked={on} onChange={() => { const p = new Set(pick); if (on) p.delete(n); else p.add(n); setPick(p); }} className="mt-1 h-4 w-4 accent-[rgb(var(--primary))]" />
                        <span className="min-w-0 text-sm">{i.qty && <b className="num">{scaleQty(i.qty, by)} </b>}{i.item}{have && <span className="ml-1.5 text-xs text-good">on the list</span>}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button type="button" onClick={toList} disabled={!pick.size || add.isPending} className={`${primaryBtn} flex-1`}><ShoppingBasket className="h-4 w-4" />Add {pick.size} to the list</button>
                <button type="button" onClick={() => setPick(pick.size ? new Set() : new Set(r.ingredients.map((_, i) => i)))} className="text-xs text-ink-muted hover:text-ink">{pick.size ? "Untick all" : "Tick all"}</button>
              </div>
              <p className="mt-2 text-xs text-ink-muted">Untick what's already in the pantry.</p>
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-5">
          {r.method.length > 0 && (
            <div>
              <h3 className="mb-3 font-semibold">Method</h3>
              <ol className="space-y-3">
                {r.method.map((m, i) => (
                  <li key={i} className="flex gap-3 text-[15px] leading-relaxed">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{i + 1}</span>
                    <span className="min-w-0 pt-0.5">{m}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {r.notes && (
            <div className="rounded-lg border-l-4 border-chart-5 bg-surface-raised/60 p-4">
              <h3 className="mb-1 text-sm font-semibold">Notes</h3>
              <p className="whitespace-pre-line text-sm text-ink-muted">{r.notes}</p>
            </div>
          )}
          {r.method.length === 0 && r.ingredients.length === 0 && r.source_kind !== "photo" && (
            <p className="text-sm text-ink-muted">Nothing written in yet. <button type="button" className="text-primary" onClick={() => onEdit(draftOf())}>Edit</button> to add it.</p>
          )}
        </div>
      </div>

      {note && (
        <div className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex justify-center lg:bottom-8">
          <span className="pointer-events-auto flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm text-bg shadow-soft">
            <Check className="h-4 w-4" />{note}
            {note.includes("shopping") && <button type="button" onClick={() => nav("/kitchen/list")} className="ml-1 font-semibold underline">View</button>}
            <button type="button" onClick={() => setNote(null)} aria-label="Dismiss"><X className="h-3.5 w-3.5" /></button>
          </span>
        </div>
      )}
    </Sheet>
  );
}
